import React from "react";
import { cloudinaryFull } from "../Footprint/cloudinary";

// Cloudinary files a PDF under resource_type "image" (its upload documentation:
// image = "Images (including animated images), PDFs, and 3D models"), so the
// resource type alone would put a PDF in a photo frame. The Bulletin and the
// Marketplace correct for this the same way.
const FILE_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "zip"];

// Formats most browsers cannot draw from the original file. The photo in the
// frame is always converted; these also need the click-through converted, or
// "open the original" downloads a file Chrome cannot show.
const NEEDS_CONVERTING = ["heic", "heif"];

function extensionOf(value) {
  const last = String(value || "").split(/[?#]/)[0].split("/").pop();
  return last.includes(".") ? last.split(".").pop().toLowerCase() : "";
}

/**
 * One photo, clip or file on the wall.
 *
 * Every photograph is shown in the SAME 3:2 frame and cropped to fill it.
 *
 * The earlier version used object-contain with a max-height, so each image
 * rendered at its own natural height — a 16:9 still, then a tall portrait
 * flyer, then a square. Scrolling that is restless: your eye has to re-find the
 * left edge and re-judge the scale on every single post. One frame means the
 * wall has a rhythm and the photographs are what change, not the layout.
 *
 * 3:2 because it is the native ratio of most cameras and phones, so the typical
 * family snapshot loses nothing. A portrait shot is centre-cropped, which is
 * the same bargain every photo feed makes; clicking through to the original is
 * the escape hatch if it ever matters.
 *
 * Video and PDF keep their own shape — a PDF dressed up as a photograph is just
 * a broken-looking photograph.
 */
function Attachment({ attachment }) {
  const url = attachment?.url || attachment?.secure_url;
  if (!url) return null;

  // The uploader stores the name as original_name; original_filename is
  // Cloudinary's own spelling, kept as a fallback.
  const name = attachment.original_name || attachment.original_filename;
  const isDocument =
    FILE_EXTENSIONS.includes(extensionOf(url)) || FILE_EXTENSIONS.includes(extensionOf(name));

  const kind = isDocument
    ? "file"
    : attachment.resource_type === "video"
    ? "video"
    : attachment.resource_type === "image"
    ? "image"
    : "file";

  if (kind === "image") {
    return (
      <a
        href={NEEDS_CONVERTING.includes(extensionOf(url)) ? cloudinaryFull(url) : url}
        target="_blank"
        rel="noopener noreferrer"
        className="block group/img"
      >
        {/* Served through Cloudinary's automatic format and size, so nobody
            downloads a 12-megapixel original to see a 620px frame. It is also
            the route for an iPhone HEIC, which Chrome cannot draw: Cloudinary
            lists HEIC as supported for upload and transformation. (Not yet
            watched end to end with a real HEIC upload.) */}
        <img
          src={cloudinaryFull(url)}
          alt="Family photo"
          loading="lazy"
          className="block w-full aspect-[3/2] object-cover bg-black/[0.04] dark:bg-white/[0.04]
                     transition-transform duration-500 ease-out
                     group-hover/img:scale-[1.015]
                     motion-reduce:transition-none motion-reduce:group-hover/img:scale-100"
        />
      </a>
    );
  }

  if (kind === "video") {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        className="block w-full aspect-[3/2] object-cover bg-black"
      >
        Your browser can’t play this video.
      </video>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-[10px] px-[14px] py-[12px] no-underline
                 bg-dupontCream dark:bg-neutral-800 border-t border-dupontGoldLt/30"
    >
      <span aria-hidden="true" className="text-[18px]">📄</span>
      <span className="text-[14px] text-dupontBlue dark:text-dupontGoldLt underline">
        {name || "Open attachment"}
      </span>
    </a>
  );
}

export default Attachment;
